import { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react"
import cx from "classnames"
import { createPortal } from "react-dom"
import { getMyMissions, getMissions } from "../services/api/v2/mission"
import { getOrgClients } from "../services/api/v2/client"
import { motion, AnimatePresence } from "framer-motion"
import BeatLoader from "react-spinners/BeatLoader"

import Tip from "./Tip"
import { getThumb, getMissionTitle } from "../utils/mission"
import { getObjectId, getRefId, isPersonalProject } from "../utils/utils"
import BlurHandler from "./BlurHandler"
import uniq from "lodash/uniq"
import SearchDropDown from "./SearchDropDown"
import { IoCloseOutline } from "react-icons/io5"
import Skeleton from "./Skeleton"
import "./DropDowns.scss"
import { addDays, subDays } from "date-fns"
import { utcToIsoString } from "../utils/dates"

const COUNT_MAX = 80
const BASE_SELECT = ["_id", "title", "projectCode", "theme.backgroundImage", "org", "isProposal", "isProposal"]

export function ClientSearch({
    app,
    orgId,
    width = 300,
    onSelect,
    selectedId,
    params = {},
    placeHolder,
    noClientsMessage,
    widthClass = "w-300",
    menuWidth,
    programTag,
    onNewClient,
    hideInactive = true,
}) {
    const to = useRef()

    const [data, setData] = useState()
    const [loading, setLoading] = useState()
    const [gettingCount, setGettingCount] = useState(true)
    const [clientObj, setClientObj] = useState()
    const [clientList, setClientList] = useState([])
    const [clientCount, setClienCount] = useState()

    const orgData = useMemo(() => {
        return app.state.orgs?.find((o) => getObjectId(o) === orgId)
    }, [app?.state?.orgs, orgId])

    const meInOrg = useMemo(() => {
        return orgData?.people?.find((p) => getRefId(p) === app.state.person._id)
    }, [orgData?.people])

    const filterClients = (clientData = []) => {
        if (!clientData.length) return []

        // Pre-compute filter conditions
        const program = programTag ? orgData?.programs?.find((p) => p._id === programTag) : null
        const programClientIds = program?.clients?.length ? new Set(program.clients) : null
        const accessibleDeptIds = meInOrg?.accessibleDepartments?.length ? new Set(meInOrg.accessibleDepartments) : null

        // SINGLE PASS - all filters applied at once
        return clientData.filter((client) => {
            // Program filter
            if (programClientIds && !programClientIds.has(client._id)) {
                return false
            }

            // Department filter
            if (accessibleDeptIds) {
                if (client.departments?.length && !client.departments.some((did) => accessibleDeptIds.has(did))) {
                    return false
                }
            }

            if (hideInactive) {
                return client.status !== "inactive" && !client.isArchived
            }

            return true
        })
    }

    const getCounts = async () => {
        const res = await getOrgClients(orgId, {
            limit: 1,
            count: true,
            select: ["_id"],
        })

        if (res.count <= 100) {
            const gc = await getOrgClients(orgId, {
                limit: 100,
                select: ["_id", "title", "isArchived", "status"],
                ...params,
            })

            const vals = gc.value

            if (onNewClient) {
                if (
                    meInOrg.permission === 2 ||
                    meInOrg.permission === 1 ||
                    (meInOrg.permission === 4 && orgData.canProjectManagersCreateOrderCodes)
                ) {
                    vals.unshift({ title: " 🔥 Add a new client...", _id: "new-client" })
                }
            }

            setClientList(filterClients(vals))
        }

        setClienCount(res.count)
        setGettingCount(false)
    }

    useEffect(() => {
        getCounts()
    }, [orgId])

    useEffect(() => {
        const gm = async () => {
            const res = await getOrgClients(orgId, {
                limit: 1,
                id: selectedId,
                select: ["_id", "title"],
                ...params,
            })

            setClientObj(res.value[0])
        }
        if (selectedId) {
            gm()
        } else {
            setClientObj(null)
        }
    }, [selectedId, orgId])

    const fetchSearchData = async (value) => {
        setLoading(true)

        const res = await getOrgClients(orgId, {
            limit: 10,
            org: orgId,
            search: value,
            select: ["_id", "title"],
            ...params,
        })

        setData(res.value)

        setLoading(false)
    }

    const onChange = (e) => {
        e.persist()

        if (!e.target?.value?.length) {
            onSelect(null)
            clearTimeout(to.current)
            setData([])
            setLoading(false)
            return
        }

        if (e.target?.value?.trim()?.length < 3) {
            clearTimeout(to.current)
            setData([])
            setLoading(false)
            return
        }

        clearTimeout(to.current)

        to.current = setTimeout(() => {
            fetchSearchData(e.target.value.trim())
        }, 500)
    }

    if (gettingCount) {
        return <Skeleton height={38} className={widthClass} style={{ borderRadius: 4 }} />
    }

    if (!clientCount && !gettingCount && !onNewClient) {
        return <>{noClientsMessage}</>
    }

    return (
        <div className="x-searcher clients">
            {clientCount <= 100 ? (
                <SearchDropDown
                    selectedObj={clientObj}
                    data={clientList}
                    wrapperClassName={cx(widthClass, {
                        "x-field-orange": clientObj,
                    })}
                    menuWidth={menuWidth}
                    placeHolder={placeHolder || "Select a client..."}
                    onSelect={(label, obj) => {
                        if (obj?._id === "new-client") {
                            onNewClient()
                        } else {
                            setClientObj(obj)
                        }
                        onSelect(obj || null)
                    }}
                    selectOnBlur={false}
                    showClearIcon={true}
                    onClear={() => {
                        setClientObj(null)
                        onSelect(null)
                    }}
                />
            ) : (
                <Dropper
                    onChange={onChange}
                    data={data}
                    loading={loading}
                    width={width}
                    defaultValue={clientObj?.title}
                    placeHolder={placeHolder || "Find a client..."}
                    type="client"
                    menuWidth={menuWidth}
                    comp={(data) => <ClientRow client={data} />}
                    widthClass={widthClass}
                    onSelect={(obj) => {
                        if (obj?._id === "new-client") {
                            onNewClient()
                        } else {
                            onSelect(obj)
                        }
                    }}
                    onClose={() => {
                        onSelect(null)
                        clearTimeout(to.current)
                        setData([])
                        setLoading(false)
                    }}
                />
            )}
        </div>
    )
}

export function MissionSearch({
    app,
    orgId,
    width,
    orgSearch,
    onSelect,
    selectedId,
    params = {},
    placeHolder,
    menuWidth,
    orgAccessCounts,
    portal,
    widthClass = "w-300",
    noStyle,
    includeOrg,
    hasIssue,
    noPersonal,
}) {
    const [data, setData] = useState()
    const [loading, setLoading] = useState()
    const to = useRef()
    const [defaultValue, setDefaultValue] = useState()
    const [selectedObj, setSelectedObj] = useState()
    const fn = orgSearch ? getMissions : getMyMissions
    const keep = (list = []) => (noPersonal ? list.filter((m) => !isPersonalProject(m)) : list)

    const [myMissionCount, setMyMissionCount] = useState(Infinity)

    const loadAll = async () => {
        const { select: selectParam, ...restParams } = params

        const select = uniq([
            "_id",
            "title",
            "projectCode",
            "theme.backgroundImage",
            "org",
            "isProposal",
            "isProcess",
            ...(selectParam || []).filter((item) => item !== "theme"),
        ])

        const res = await fn({
            limit: COUNT_MAX,
            org: orgId,
            exists: ["isTemplate:false", "isModel:false"],
            ...restParams,
            select: select,
        })

        if (selectedId) {
            const sd = res.value.find((m) => m._id === selectedId)

            setSelectedObj(sd ? { ...sd, missionName: getMissionTitle(sd) } : null)
        }

        const rv = keep(res.value).map((d) => {
            const missionName = getMissionTitle(d)
            const orgName = app.state.orgs.find((o) => o._id === getObjectId(d.org))?.title
            return { ...d, missionName: missionName, orgName: orgName }
        })

        setData(rv)
    }

    const checkCount = async () => {
        const c = orgAccessCounts
            ? { count: orgAccessCounts.missionCount }
            : await getMyMissions({
                  count: true,
                  limit: 1,
                  select: ["_id"],
                  endDate: `gte ${utcToIsoString(subDays(new Date(), 180))}`,
              })

        setMyMissionCount(c.count)

        if (c.count < COUNT_MAX) {
            loadAll()
        }
    }

    useEffect(() => {
        if (orgAccessCounts) {
            if (orgAccessCounts?.missionCount < COUNT_MAX) {
                loadAll()
            }
        } else {
            checkCount()
        }
    }, [orgId, orgAccessCounts])

    useEffect(() => {
        const gm = async () => {
            const res = await fn({
                limit: 1,
                id: selectedId,
                select: BASE_SELECT,
                ...params,
            })

            const m = res.value[0]

            if (!m) {
                setDefaultValue("Mission not found...")
            } else {
                setSelectedObj({ ...m, missionName: getMissionTitle(m) })
                setDefaultValue(getMissionTitle(m))
            }
        }
        if (selectedId) {
            gm()
        }
    }, [selectedId, orgAccessCounts])

    const fetchData = async (value) => {
        setLoading(true)

        const res = await fn({
            limit: 10,
            org: orgId,
            search: value,
            exists: ["isTemplate:false", "isModel:false"],
            select: BASE_SELECT,
            ...params,
        })

        setData(keep(res.value))

        setLoading(false)
    }

    const onChange = (e) => {
        e.persist()

        if (!e.target?.value?.length) {
            onSelect(null)
            clearTimeout(to.current)
            setData([])
            setLoading(false)
            return
        }

        if (e.target?.value?.trim()?.length < 3) {
            clearTimeout(to.current)
            setData([])
            setLoading(false)
            return
        }

        clearTimeout(to.current)

        to.current = setTimeout(() => {
            fetchData(e.target.value.trim())
        }, 500)
    }

    if (orgAccessCounts?.missionCount <= COUNT_MAX || myMissionCount < COUNT_MAX) {
        if (loading) {
            return <Skeleton height={38} className={widthClass} style={{ borderRadius: 4 }} />
        }
        return (
            <SearchDropDown
                key={selectedObj?._id}
                menuWidth={menuWidth}
                style={{ width: width }}
                wrapperClassName={cx(width ? null : widthClass || "w-200", {
                    "x-field-orange": Boolean(selectedObj) && !noStyle,
                    "x-field-red": hasIssue,
                })}
                usePortal={portal}
                data={data}
                noResultsText={loading ? "Loading..." : "No results found..."}
                labelKey={"missionName"}
                placeHolder={placeHolder || "Find a mission..."}
                onSelect={(label, obj) => {
                    setSelectedObj(obj)
                    onSelect(obj)
                }}
                selectedObj={selectedObj}
                replaceTemplate={true}
                template={(mission) => {
                    return (
                        <div className="x-searcher-mission-row">
                            <div className="dna-round-avatar smaller" style={{ backgroundImage: getThumb(mission) }} />{" "}
                            <div>
                                {mission.projectCode || ""} {mission.title}{" "}
                                <div className="dna-light-text dna-smaller-text">
                                    {mission.isProposal && "Opportunity"} {mission.isProcess && "Process"}
                                </div>
                                {includeOrg && mission.orgName && (
                                    <div className="dna-light-text dna-smaller-text">{mission.orgName}</div>
                                )}
                            </div>
                        </div>
                    )
                }}
                showClearIcon={true}
                onClear={() => {
                    onSelect(null)
                    setSelectedObj(null)
                    clearTimeout(to.current)
                }}
            />
        )
    }

    return (
        <div className="x-searcher missions">
            <Dropper
                hasIssue={hasIssue}
                onChange={onChange}
                data={data}
                loading={loading}
                width={width}
                widthClass={widthClass}
                defaultValue={defaultValue}
                menuWidth={menuWidth}
                type="mission"
                placeHolder={placeHolder || "Find a mission..."}
                comp={(data) => <MissionRow mission={data} />}
                onSelect={(obj) => {
                    onSelect(obj)
                }}
                onClose={() => {
                    onSelect(null)
                    clearTimeout(to.current)
                    setData([])
                    setLoading(false)
                }}
            />
        </div>
    )
}

export const Dropper = ({
    onChange,
    placeHolder = "Search...",
    data,
    comp,
    loading,
    width,
    onClose,
    onSelect,
    defaultValue,
    widthClass,
    menuWidth,
    type,
    hasIssue,
}) => {
    const inputRef = useRef(null)
    const rafRef = useRef(null)
    const [open, setOpen] = useState(false)
    const [position, setPosition] = useState(null)
    const [value, setValue] = useState()
    useEffect(() => {
        setOpen(!!data?.length || loading)
    }, [data])

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])

    // Measure input position
    useLayoutEffect(() => {
        if (!open || !inputRef.current) return

        updatePosition(inputRef, setPosition)

        const parents = getScrollParents(inputRef.current)

        const onScroll = () => {
            cancelAnimationFrame(rafRef.current)
            rafRef.current = requestAnimationFrame(() => updatePosition(inputRef, setPosition))
        }

        parents.forEach((p) => p.addEventListener("scroll", onScroll))
        window.addEventListener("resize", onScroll)

        return () => {
            parents.forEach((p) => p.removeEventListener("scroll", onScroll))
            window.removeEventListener("resize", onScroll)
            cancelAnimationFrame(rafRef.current)
        }
    }, [open])

    const onFocus = (e) => {
        if (data?.length && e.target.value) {
            setOpen(true)
        }
    }

    const portalRoot = document.getElementById("dna-body")

    const changeHandler = (e) => {
        onChange(e)
        setValue(e.target?.value)
    }

    return (
        <div className={"x-searcher-search " + widthClass}>
            <span
                className={cx({
                    "x-field-orange": value?.length > 2,
                    "x-f-red": hasIssue,
                })}
            >
                <input
                    ref={inputRef}
                    onChange={changeHandler}
                    placeholder={placeHolder}
                    value={value}
                    onFocus={onFocus}
                />
                {value?.length > 0 && (
                    <Tip
                        followCursor
                        position="right"
                        title={"Clear field..."}
                        noFlip={true}
                        onClick={() => {
                            setValue("")
                            onClose()
                        }}
                    >
                        <IoCloseOutline />
                    </Tip>
                )}
            </span>

            {open &&
                position &&
                portalRoot &&
                createPortal(
                    <BlurHandler
                        onBlur={() => {
                            setOpen(false)
                        }}
                    >
                        <div
                            className="x-searcher-search-menu"
                            style={{
                                top: position.top,
                                left: position.left,
                                width: menuWidth || width || position.width,
                                minWidth: menuWidth,
                                maxHeight: position.maxHeight, // Automatically shrinks below viewport
                                overflowY: "auto", // Enables internal scrolling when clamped
                                zIndex: 9999,
                            }}
                        >
                            {loading && (
                                <div
                                    className="x-searcher-search-menu-item"
                                    style={{ paddingTop: 10, paddingBottom: 5 }}
                                >
                                    <BeatLoader loading="true" color="#666666" size={10} speedMultiplier={0.5} />
                                </div>
                            )}
                            {!loading && data.length === 0 && (
                                <div className="x-searcher-search-menu-item">No results found</div>
                            )}
                            <AnimatePresence>
                                {data.map((d, i) => (
                                    <motion.div
                                        className={cx("x-searcher-search-menu-item", type)}
                                        key={d._id}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.05, delay: 0.03 * i }}
                                        onClick={() => {
                                            setValue(d.title)
                                            onSelect(d)
                                            setOpen(false)
                                        }}
                                    >
                                        {comp(d)}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </BlurHandler>,
                    portalRoot,
                )}
        </div>
    )
}

const MissionRow = ({ mission }) => {
    return (
        <div className="x-searcher-mission-row">
            <div className="dna-round-avatar smaller" style={{ backgroundImage: getThumb(mission) }} />{" "}
            {mission.projectCode} {mission.title} {mission.isProposal && "(Opportunity)"}
        </div>
    )
}
const ClientRow = ({ client }) => {
    return client.title
}

const updatePosition = (inputRef, setPosition) => {
    if (!inputRef.current) return

    const rect = inputRef.current.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const padding = 10 // Margin from the bottom of the viewport

    // Calculate available space below the input
    const top = rect.bottom
    const maxAvailableHeight = windowHeight - top - padding

    setPosition({
        top: top + window.scrollY, // Accounts for document scrolling if needed
        left: rect.left + window.scrollX,
        width: rect.width,
        // Clamp max height so it never overflows below the fold
        maxHeight: Math.min(600, Math.max(100, maxAvailableHeight)),
    })
}
const getScrollParents = (element) => {
    const parents = []
    let parent = element.parentElement

    while (parent) {
        const style = getComputedStyle(parent)
        const overflow = style.overflow + style.overflowY + style.overflowX

        if (/(auto|scroll)/.test(overflow)) {
            parents.push(parent)
        }

        parent = parent.parentElement
    }

    parents.push(window)
    return parents
}
